/**
 * Runtime + build-time configuration for the signup SPA.
 *
 * In a deployed container the values come from `window.__SIGNUP_CONFIG__`, which
 * the nginx entrypoint writes into /config.js from the App Service slot's
 * environment variables at container start (see config.js.template +
 * docker-entrypoint.sh). For local `yarn dev` / `yarn build` there is no
 * container, so each field falls back to the build-time `import.meta.env`
 * (VITE_*) values from `.env`.
 */
export interface SignupConfig {
  /** AAD app the admin signs into via MSAL; its token audience must match the backend. */
  mainAppId: string;
  backendUrl: string;
  webAppUrl: string;
  /** Optional hardcoded admin-consent callback URI; empty = derive from window.location. */
  signUpRedirectUri: string;
}

type RuntimeConfig = Partial<SignupConfig>;

declare global {
  interface Window {
    __SIGNUP_CONFIG__?: RuntimeConfig;
  }
}

const WEBAPP_URL_DEFAULT = 'https://my.mnspro.cloud';

/**
 * Pure resolver: prefer a non-empty runtime value, else the build-time value,
 * else the default. Free of `import.meta` / `window` so it is trivially testable.
 */
export function resolveConfig(
  runtime: RuntimeConfig,
  buildEnv: Record<string, string | undefined>,
): SignupConfig {
  const pick = (
    runtimeValue: string | undefined,
    buildValue: string | undefined,
    fallback = '',
  ): string =>
    runtimeValue && runtimeValue.length > 0
      ? runtimeValue
      : buildValue && buildValue.length > 0
        ? buildValue
        : fallback;

  return {
    mainAppId: pick(runtime.mainAppId, buildEnv.VITE_SIGNUP_MAIN_APP_ID),
    backendUrl: pick(runtime.backendUrl, buildEnv.VITE_BACKEND_URL),
    webAppUrl: pick(runtime.webAppUrl, buildEnv.VITE_WEBAPP_URL, WEBAPP_URL_DEFAULT),
    signUpRedirectUri: pick(runtime.signUpRedirectUri, buildEnv.VITE_SIGNUP_REDIRECT_URI),
  };
}

const runtime: RuntimeConfig =
  (typeof window !== 'undefined' && window.__SIGNUP_CONFIG__) || {};

export const config: SignupConfig = resolveConfig(
  runtime,
  import.meta.env as unknown as Record<string, string | undefined>,
);
